import { useCallback, useEffect, useRef, useState } from "react";
import { Check, X, Video, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import api from "../pages/services/api";
import useAuthStore from "../stores/authStore";

import { Loader } from "@/components/ui/loader";
// How often we ask the backend whether anyone is waiting. Slow enough to be
// cheap; fast enough that a learner doesn't sit in the LMS waiting room
// awkwardly while the host is heads-down somewhere else in the dashboard.
const POLL_INTERVAL_MS = 4000;

/**
 * Pending-join-request modal for hosts (admin / instructor of the live class).
 *
 * Shows a centered, opaque-backdrop modal whenever there is at least one
 * learner knocking on a live-class door. The host clicks Allow / Deny per
 * row; the modal auto-closes once the queue is empty. Mounted in the shared
 * Layout so it pops up wherever the host is in the dashboard, not just on
 * the live-class page. Self-mutes for non-host roles.
 *
 * The host can temporarily collapse the modal to a small floating chip
 * ("X waiting") via the Hide button — the chip stays visible (unmissable)
 * and clicking it re-opens the modal.
 */
export default function PendingJoinRequestsNotifier() {
  const user = useAuthStore((s) => s.user);
  const role = (user?.role || "").toLowerCase();
  const isHostRole = role === "admin" || role === "instructor";

  const [requests, setRequests] = useState([]);
  const [decidingId, setDecidingId] = useState(null);
  // The host can collapse the centered modal to a floating chip if they
  // need to do something else first; the chip is sticky until the queue empties.
  const [collapsed, setCollapsed] = useState(false);
  // Tracks request IDs we've already toasted so we don't re-buzz on every
  // poll — only a freshly-arrived request rings the bell.
  const seenIdsRef = useRef(new Set());

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get(`/live-courses/admin/pending-join-requests`);
      const all = Array.isArray(res.data?.requests) ? res.data.requests : [];
      // Live-session rows only. A full-screen modal that hijacks the dashboard
      // is right for someone waiting at a meeting door right now — they're
      // staring at a waiting room and the request expires. It is wrong for
      // "would like to enrol", which has no deadline; those now live in the
      // course's Learners tab, where the admin can see them in context.
      const list = all.filter((r) => r.liveCourseId != null);
      setRequests(list);

      // Toast on net-new requests so a host who's heads-down in another tab
      // gets a nudge instead of having to watch the modal.
      const newOnes = list.filter((r) => !seenIdsRef.current.has(r.id));
      if (newOnes.length > 0) {
        for (const r of newOnes) {
          // Two row variants: session-level ("wants to join <class>") and
          // course-level ("wants to enroll in <course>"). The backend
          // ships them in the same payload; branch the copy on whichever
          // FK is set.
          const target = r.liveCourseTitle || r.courseTitle || "this";
          const verb = r.liveCourseId ? "wants to join" : "wants to enroll in";
          toast(`${r.learnerName || r.learnerEmail || "A learner"} ${verb} "${target}"`, {
            icon: "🔔",
            duration: 6000,
          });
        }
        // Auto-uncollapse on a brand-new request — the host must actively
        // dismiss again if they want to ignore it.
        setCollapsed(false);
      }
      seenIdsRef.current = new Set(list.map((r) => r.id));
    } catch (err) {
      // Don't toast on poll failures; they're noisy and the next tick retries.
      console.warn("[JoinRequestsNotifier] poll failed", err);
    }
  }, []);

  useEffect(() => {
    if (!isHostRole) return undefined;

    fetchPending();
    const interval = setInterval(fetchPending, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isHostRole, fetchPending]);

  const decide = async (request, decision) => {
    setDecidingId(request.id);
    try {
      await api.patch(`/live-courses/join-requests/${request.id}`, { decision });
      // Drop it locally so the modal updates instantly; the next poll will
      // reconcile if anything changed server-side.
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      seenIdsRef.current.delete(request.id);
      toast.success(
        decision === "approve"
          ? `Admitted ${request.learnerName || "learner"}`
          : `Denied ${request.learnerName || "learner"}`
      );
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Could not update request";
      toast.error(msg);
      // If the row is already decided/expired (409/404), drop it from view.
      const status = err.response?.status;
      if (status === 404 || status === 409) {
        setRequests((prev) => prev.filter((r) => r.id !== request.id));
        seenIdsRef.current.delete(request.id);
      }
    } finally {
      setDecidingId(null);
    }
  };

  const admitAll = async () => {
    // Sequential so a single failure doesn't abort the rest, and so per-row
    // toasts don't all stack at once.
    for (const r of requests) {
      await decide(r, "approve");
    }
  };

  if (!isHostRole) return null;
  if (requests.length === 0) return null;

  // Collapsed chip — small, sticky, obviously clickable so the host can't
  // forget there's still a queue.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-6 right-6 z-[80] flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-2xl ring-2 ring-primary/30 animate-pulse"
        title="Open the join requests panel"
      >
        <BellRing className="h-4 w-4" />
        {requests.length} waiting to join — review
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pending-join-requests-title"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
        <div className="flex items-center justify-between gap-2 border-b border-border bg-primary px-5 py-3 text-primary-foreground">
          <div className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            <h2 id="pending-join-requests-title" className="text-base font-semibold">
              {requests.length} {requests.length === 1 ? "person" : "people"} waiting to join
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="rounded-md px-2 py-1 text-xs font-medium hover:bg-white/10"
          >
            Hide
          </button>
        </div>

        <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">
          {requests.map((r) => (
            <li key={r.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-base font-medium leading-tight">
                    {r.learnerName || r.learnerEmail || "Unknown learner"}
                  </div>
                  {r.learnerEmail && r.learnerName && (
                    <div className="mt-0.5 text-xs text-muted-foreground">{r.learnerEmail}</div>
                  )}
                  <div
                    className="mt-1 line-clamp-2 text-sm text-muted-foreground"
                    title={r.liveCourseTitle || r.courseTitle}
                  >
                    {r.liveCourseId ? "Wants to join" : "Wants to enroll in"}:{" "}
                    <span className="font-medium text-foreground">
                      {r.liveCourseTitle || r.courseTitle || "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => decide(r, "approve")}
                  disabled={decidingId === r.id}
                  className="flex-1"
                >
                  {decidingId === r.id ? (
                    <Loader size="xs" className="mr-1.5" />
                  ) : (
                    <Check className="mr-1.5 h-4 w-4" />
                  )}
                  Allow
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => decide(r, "deny")}
                  disabled={decidingId === r.id}
                  className="flex-1"
                >
                  <X className="mr-1.5 h-4 w-4" />
                  Deny
                </Button>
              </div>
            </li>
          ))}
        </ul>

        {requests.length > 1 && (
          <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
            <Button
              size="sm"
              onClick={admitAll}
              disabled={decidingId !== null}
            >
              <Check className="mr-1.5 h-4 w-4" />
              Allow all
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
